import React, { useState, useEffect } from "react";
import {
  Divider,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  CircularProgress,
  Autocomplete,
  TextField,
} from "@mui/material";
import HelpIcon from "@mui/icons-material/Help";
import { ListWrapper, WidgetsWrapper, ListItem, InfoText, GroupTitle } from "./styles";
import ModalSimpleButton from "../../components/Modal/ModalSimpleButton";
import UserApi from "../../api/Admin/User";
import SiteApi from "../../api/Admin/Site";
import { ApplicationPreferences, Site } from "../../api/types";
import { usePreferences } from "../../context/PreferencesContext";
import { useAuth } from "../../context/AuthContext";
import { InternalUserRoles, Role } from "../../constants/roles";
import AuthGate from "../../routing/components/AuthGate";
import { User as UserInfo } from "../../api/types";

interface ModalContent {
  title: string;
  message: string;
}

const MODAL_CONTENT_INITIAL_STATE: ModalContent = {
  title: "",
  message: "",
};

const UPrefs = () => {
  const { identity, roles, hasAnyRole } = useAuth();
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [sites, setSites] = useState<Site[]>([]);
  const {
    userPreferences,
    setUserPreferences,
    updateUserPreferences,
    modalContent,
    setModalContent,
    isLoading,
  } = usePreferences();

  const isXOMUser = hasAnyRole(InternalUserRoles);

  useEffect(() => {
    if (modalContent?.title !== "") {
      setIsModalOpen(true);
    }
  }, [modalContent.title]);

  useEffect(() => {
    if (isXOMUser) {
      SiteApi.getAllActive().then((res) => {
        const sites = res
          .filter((d: Site) => {
            return isXOMUser ? !d.isExternalSite : true;
          })
          .sort((a: Site, b: Site) => a.siteName?.localeCompare(b?.siteName ?? ""));

        setSites(sites);
      });
    }
  }, [isXOMUser]);

  useEffect(() => {
    const getUserInfo = async () => {
      const response = await UserApi.getMe();
      const copiedResponse = JSON.parse(JSON.stringify(response));
      setUserInfo(copiedResponse);
    };

    getUserInfo();
  }, [identity]);

  return (
    <>
      <span className="pageheader">User Preferences</span>
      <Divider className="dividerbar" />
      <Box display="flex" flexDirection="column" gap={3}>
        <UserInfoSection userInfo={userInfo} />
        <UserRolesSection userRoles={roles || []} />
        <AuthGate>
          <NotificationsSection
            userPreferences={userPreferences}
            setUserPreferences={setUserPreferences}
          />
          <WidgetsSection
            userPreferences={userPreferences}
            setUserPreferences={setUserPreferences}
          />
        </AuthGate>
        <PreferencesSection
          isXOMUser={isXOMUser}
          sites={sites}
          userPreferences={userPreferences}
          setUserPreferences={setUserPreferences}
          modalContent={modalContent}
          setModalContent={setModalContent}
        />

        <AuthGate>
          <Box display="flex" alignItems="center" marginTop={2}>
            <Button
              variant="contained"
              onClick={updateUserPreferences}
              type="button"
              disabled={isLoading}>
              Save Changes
            </Button>
          </Box>
        </AuthGate>
      </Box>
      {isModalOpen && (
        <ModalSimpleButton
          title={modalContent?.title}
          buttonText="Ok"
          buttonAction={() => {
            setModalContent(MODAL_CONTENT_INITIAL_STATE);
            setIsModalOpen(false);
          }}
          setOpen={setIsModalOpen}
          open={isModalOpen}>
          <Typography style={{ padding: "0 16px", fontSize: 18, textAlign: "center" }}>
            {modalContent?.message}
          </Typography>
        </ModalSimpleButton>
      )}
    </>
  );
};

export default UPrefs;

interface UserInfoSectionProps {
  userInfo: UserInfo | null;
}

const UserInfoSection = ({ userInfo }: UserInfoSectionProps) => {
  return (
    <Box display="flex" flexDirection="column" gap={1}>
      <GroupTitle>User Info:</GroupTitle>
      {!userInfo ? (
        <Box display="flex" gap={2} alignItems="center">
          <CircularProgress size={20} />
          <Typography>Loading User Info...</Typography>
        </Box>
      ) : (
        <>
          <InfoText>
            <strong>Name:</strong> {userInfo?.surname}, {userInfo?.givenName}
          </InfoText>
          <InfoText>
            <strong>Display Name:</strong> {userInfo?.displayName}
          </InfoText>
          <InfoText>
            <strong>Email:</strong> {userInfo?.mail}
          </InfoText>
          <AuthGate>
            <InfoText>
              <strong>Job Title:</strong> {userInfo?.jobTitle}
            </InfoText>
            <InfoText>
              <strong>Office Location:</strong>{" "}
              {userInfo?.officeLocation ? userInfo?.officeLocation : "N/A"}
            </InfoText>
            <InfoText>
              <strong>Business Phones:</strong>{" "}
              {userInfo?.businessPhones ? userInfo?.businessPhones?.join(", ") : "N/A"}
            </InfoText>
            <InfoText>
              <strong>Mobile Phones:</strong>{" "}
              {userInfo?.mobilePhones ? userInfo?.mobilePhones?.join(", ") : "N/A"}
            </InfoText>
          </AuthGate>
        </>
      )}
    </Box>
  );
};

interface UserRolesSectionProps {
  userRoles: Role[];
}

const UserRolesSection = ({ userRoles }: UserRolesSectionProps) => {
  return (
    <Box display="flex" flexDirection="column" gap={1}>
      <GroupTitle>User Roles:</GroupTitle>
      <ListWrapper>
        {userRoles?.sort().map((role) => (
          <ListItem key={role}>{role}</ListItem>
        ))}
      </ListWrapper>
      <AuthGate>
        <Typography fontSize={18}>
          Click{" "}
          <a
            href="https://vega.identitynow.com/ui/d/request-center"
            target="_blank"
            rel="noopener noreferrer">
            here
          </a>{" "}
          to join an Active Directory group.
        </Typography>
      </AuthGate>
    </Box>
  );
};

interface NotificationSectisonProps {
  userPreferences: ApplicationPreferences;
  setUserPreferences: React.Dispatch<React.SetStateAction<ApplicationPreferences>>;
}

const NotificationsSection = ({
  userPreferences,
  setUserPreferences,
}: NotificationSectisonProps) => {
  return (
    <Box display="flex" flexDirection="column" gap={1}>
      <GroupTitle>Notifications:</GroupTitle>
      <FormControlLabel
        label="Receive Email Notifications"
        onChange={(e) =>
          setUserPreferences((prevState) => {
            return {
              ...prevState,
              receiveEmailNotifications: (e.target as HTMLInputElement).checked,
            };
          })
        }
        checked={userPreferences?.receiveEmailNotifications}
        control={<Checkbox />}
      />
      <FormControlLabel
        label="Receive Daily Update"
        onChange={(e) =>
          setUserPreferences((prevState) => {
            return {
              ...prevState,
              receiveDailyUpdate: (e.target as HTMLInputElement).checked,
            };
          })
        }
        checked={userPreferences?.receiveDailyUpdate}
        control={<Checkbox />}
      />
    </Box>
  );
};

interface WidgetsSectionProps {
  userPreferences: ApplicationPreferences;
  setUserPreferences: React.Dispatch<React.SetStateAction<ApplicationPreferences>>;
}

const WidgetsSection = ({ userPreferences, setUserPreferences }: WidgetsSectionProps) => {
  return (
    <Box display="flex" flexDirection="column" gap={1}>
      <GroupTitle>Widgets:</GroupTitle>
      <WidgetsWrapper>
        <FormControlLabel
          label="Show My Recent Samples widget"
          onChange={(e) =>
            setUserPreferences((prevState) => {
              return {
                ...prevState,
                showRecentSamplesWidget: (e.target as HTMLInputElement).checked,
              };
            })
          }
          checked={userPreferences?.showRecentSamplesWidget}
          control={<Checkbox />}
        />

        <FormControlLabel
          label="Show My Recent Test Submissions widget"
          onChange={(e) =>
            setUserPreferences((prevState) => {
              return {
                ...prevState,
                showRecentTestsWidget: (e.target as HTMLInputElement).checked,
              };
            })
          }
          checked={userPreferences?.showRecentTestsWidget}
          control={<Checkbox />}
        />

        <FormControlLabel
          label="Show My Recent Work Requests widget"
          onChange={(e) =>
            setUserPreferences((prevState) => {
              return {
                ...prevState,
                showWorkRequestWidget: (e.target as HTMLInputElement).checked,
              };
            })
          }
          checked={userPreferences?.showWorkRequestWidget}
          control={<Checkbox />}
        />

        <FormControlLabel
          label="Show My Recent Blend Studies widget"
          onChange={(e) =>
            setUserPreferences((prevState) => {
              return {
                ...prevState,
                showRecentBlendsWidget: (e.target as HTMLInputElement).checked,
              };
            })
          }
          checked={userPreferences?.showRecentBlendsWidget}
          control={<Checkbox />}
        />
      </WidgetsWrapper>
    </Box>
  );
};

interface PreferencesSectionProps {
  isXOMUser: boolean;
  sites: Site[];
  userPreferences: ApplicationPreferences;
  setUserPreferences: React.Dispatch<React.SetStateAction<ApplicationPreferences>>;
  modalContent: ModalContent;
  setModalContent: React.Dispatch<React.SetStateAction<ModalContent>>;
}
const PreferencesSection = ({
  sites,
  userPreferences,
  setUserPreferences,
  setModalContent,
}: PreferencesSectionProps) => {
  const readOnlyHomeSite = (
    <Typography fontSize={18} marginTop={1}>
      <strong>Home Site:</strong> {userPreferences?.homeSiteName ?? "N/A"}
    </Typography>
  );

  return (
    <Box display="flex" flexDirection="column" gap={1}>
      <GroupTitle>Preferences:</GroupTitle>
      <FormControlLabel
        label="Keep Sample Registration Fields Populated"
        onChange={(e) =>
          setUserPreferences((prevState) => {
            return {
              ...prevState,
              isSampleRetainMetadata: (e.target as HTMLInputElement).checked,
            };
          })
        }
        checked={userPreferences?.isSampleRetainMetadata ?? false}
        control={<Checkbox />}
      />
      <AuthGate fallback={readOnlyHomeSite}>
        <Box display="flex" marginTop={2} gap={5}>
          <Autocomplete
            size="small"
            style={{ width: "30rem" }}
            id="site-select"
            disablePortal
            noOptionsText={"Loading Sites..."}
            options={sites.map((site) => site.siteName)}
            getOptionLabel={(option) => option || ""}
            onChange={(e, value) =>
              setUserPreferences((prevState) => {
                return {
                  ...prevState,
                  homeSiteName: value || "",
                };
              })
            }
            autoHighlight
            autoSelect
            value={userPreferences?.homeSiteName || ""}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                size="small"
                label="Home Site"
                InputProps={{ ...params.InputProps }}
              />
            )}
          />
          <HelpIcon
            style={{ marginTop: ".5rem", color: "blue" }}
            onClick={(e) =>
              setModalContent({
                title: "Home Site Info",
                message: "Select a home site to filter location options",
              })
            }
          />
        </Box>
      </AuthGate>
    </Box>
  );
};
